import { useState, useEffect, useMemo, useCallback } from 'react';
import { X, AlertCircle, Minus } from 'lucide-react';
import { Input, Button, Checkbox, Alert, AlertDescription } from '@predoc/cow';
import { ColumnDef } from '@tanstack/react-table';
import { useDebounce } from '@/hooks';
import { useGetPatients } from '@/feature/patients/hooks/patientsAPI';
import { ExistingPatient, ProfileFilterState } from '@/feature/patients/types';
import { SelectedPatient } from '../types';
import {
  Pagination,
  PaginationState,
  ProfileBadge,
  DataTable,
  TableSkeleton,
} from '@/components';
import { ProfileFilterDropdown } from '@/feature/patients/components/patient-list/ProfileFilterDropdown';
import { getProfileFilterSearchParams } from '@/feature/patients/components/patient-list/profileFilter';
import { formatDate } from '@/utils';

interface PatientSelectionTableProps {
  selectedPatients: SelectedPatient[];
  onSelectionChange: (patients: SelectedPatient[]) => void;
  validationError?: string;
}

// Empty state component defined outside to prevent unnecessary rerenders
// Added padding-top to prevent image cutoff - DataTable renders with p-0, so padding is inside
const EmptyState = () => (
  <div className="flex min-h-[300px] flex-col items-center justify-center px-4 pt-8 pb-12">
    <img
      src="/suggested-providers.png"
      alt="No patients found"
      className="mb-4 max-w-[200px]"
    />
    <h3 className="text-lg font-semibold">No Patients Found</h3>
    <p className="text-muted-foreground text-sm">Try adjusting your search.</p>
  </div>
);

export const PatientSelectionTable = ({
  selectedPatients,
  onSelectionChange,
  validationError,
}: PatientSelectionTableProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState<PaginationState>({
    page: 0,
    pageSize: 5,
  });
  const [profileFilter, setProfileFilter] = useState<ProfileFilterState>({
    profileFilterMode: 'exclude',
    excludeOrganizationProfileIds: [],
  });

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Reset to first page when search or profile filter changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, [debouncedSearchTerm, profileFilter]);

  const profileFilterSearchParams = useMemo(
    () => getProfileFilterSearchParams(profileFilter),
    [profileFilter],
  );

  const { data: patientsData, isLoading } = useGetPatients({
    search: {
      terms: debouncedSearchTerm ? [debouncedSearchTerm] : [],
      ...profileFilterSearchParams,
    },
    pagination: {
      offset: pagination.page * pagination.pageSize,
      limit: pagination.pageSize,
    },
    includeRequests: false,
  });

  const patients: ExistingPatient[] = patientsData?.patients || [];
  const totalCount = patientsData?.totalCount || 0;

  const selectedIds = useMemo(
    () => new Set(selectedPatients.map((p) => p.id)),
    [selectedPatients],
  );

  const allVisibleSelected = useMemo(() => {
    if (patients.length === 0) return false;
    return patients.every((patient) => selectedIds.has(patient.id));
  }, [patients, selectedIds]);

  const someVisibleSelected = useMemo(() => {
    if (patients.length === 0) return false;
    const selectedCount = patients.filter((patient) =>
      selectedIds.has(patient.id),
    ).length;
    return selectedCount > 0 && selectedCount < patients.length;
  }, [patients, selectedIds]);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        // Add all visible patients to selection
        const newPatients = patients.filter(
          (patient) => !selectedIds.has(patient.id),
        );
        const patientsToAdd: SelectedPatient[] = newPatients.map((patient) => ({
          id: patient.id,
          firstName: patient.firstName,
          lastName: patient.lastName,
          dateOfBirth: patient.dateOfBirth,
          profileName: patient.organizationProfile?.displayName || '',
          profileColor: patient.organizationProfile?.avatarType,
        }));
        onSelectionChange([...selectedPatients, ...patientsToAdd]);
      } else {
        // Remove all visible patients from selection
        const visibleIds = new Set(patients.map((p) => p.id));
        onSelectionChange(
          selectedPatients.filter((p) => !visibleIds.has(p.id)),
        );
      }
    },
    [patients, selectedIds, selectedPatients, onSelectionChange],
  );

  const handleSelectPatient = useCallback(
    (patient: SelectedPatient, checked: boolean) => {
      if (checked) {
        onSelectionChange([...selectedPatients, patient]);
      } else {
        onSelectionChange(selectedPatients.filter((p) => p.id !== patient.id));
      }
    },
    [selectedPatients, onSelectionChange],
  );

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Column definitions for DataTable
  const columns: ColumnDef<ExistingPatient>[] = useMemo(
    () => [
      {
        id: 'select',
        meta: {
          className: '!min-w-0 !pr-1',
        },
        header: () => (
          <div className="w-8">
            {someVisibleSelected && !allVisibleSelected ? (
              <div
                className="border-primary bg-primary flex h-4 w-4 cursor-pointer items-center justify-center rounded-sm border"
                onClick={() => handleSelectAll(false)}
                role="checkbox"
                aria-checked="mixed"
                aria-label="Select all patients"
              >
                <Minus className="text-primary-foreground h-3 w-3" />
              </div>
            ) : (
              <Checkbox
                checked={allVisibleSelected}
                onCheckedChange={handleSelectAll}
                aria-label="Select all patients"
                className="checkbox-clean"
              />
            )}
          </div>
        ),
        cell: ({ row }) => {
          const patient = row.original;
          const isSelected = selectedIds.has(patient.id);
          const selectedPatient: SelectedPatient = {
            id: patient.id,
            firstName: patient.firstName,
            lastName: patient.lastName,
            dateOfBirth: patient.dateOfBirth,
            profileName: patient.organizationProfile?.displayName || '',
            profileColor: patient.organizationProfile?.avatarType,
          };

          return (
            <div>
              <Checkbox
                checked={isSelected}
                onCheckedChange={(checked: boolean) =>
                  handleSelectPatient(selectedPatient, checked)
                }
                aria-label={`Select ${patient.firstName} ${patient.lastName}`}
                className="checkbox-clean"
              />
            </div>
          );
        },
        enableSorting: false,
        size: 40,
      },
      {
        accessorKey: 'fullName',
        meta: {
          className: '!pl-1',
        },
        enableSorting: false,
        accessorFn: (row) => {
          return `${row.firstName || ''} ${row.lastName || ''}`.trim();
        },
        header: () => (
          <span className="text-muted-foreground font-normal">
            Patient (DOB)
          </span>
        ),
        cell: ({ row }) => {
          const patient = row.original;
          return (
            <div>
              <span className="font-normal">
                {patient.firstName} {patient.lastName}
              </span>{' '}
              <span className="font-normal">
                ({formatDate(patient.dateOfBirth)})
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: 'organizationProfile',
        enableSorting: false,
        header: () => (
          <div className="text-muted-foreground flex items-center gap-1 font-normal">
            Profile
            <ProfileFilterDropdown
              profileFilter={profileFilter}
              onProfileFilterChange={setProfileFilter}
            />
          </div>
        ),
        cell: ({ row }) => (
          <ProfileBadge
            name={row.original.organizationProfile?.displayName || ''}
            color={row.original.organizationProfile?.avatarType}
          />
        ),
      },
    ],
    [
      allVisibleSelected,
      someVisibleSelected,
      selectedIds,
      handleSelectAll,
      handleSelectPatient,
      profileFilter,
    ],
  );

  return (
    <div>
      <div className="mb-4">
        <label className="mb-1 block text-sm font-normal">Patient Search</label>
        <div className="relative w-1/2">
          <Input
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="ex. Janet Doe"
            className="pr-8"
          />
          {searchTerm && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearSearch}
              className="absolute top-1/2 right-1 h-6 w-6 -translate-y-1/2 p-0"
            >
              <X className="size-3" />
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <TableSkeleton rows={pagination.pageSize} columns={3} />
      ) : (
        <>
          <DataTable
            columns={columns}
            data={patients}
            emptyState={<EmptyState />}
          />

          <div className="mt-4 mb-8 flex items-center justify-between">
            <span className="text-muted-foreground text-sm">
              ({selectedPatients.length}) patients selected
            </span>
            <Pagination
              pagination={pagination}
              setPagination={setPagination}
              totalCount={totalCount}
              pageSizeOptions={[5, 10, 20]}
            />
          </div>

          {validationError ? (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="size-4" />
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          ) : null}
        </>
      )}
    </div>
  );
};
